import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { GatedButton } from '../components/ui/GatedButton'
import { Input, TextArea } from '../components/ui/Input'
import { SectionLabel } from '../components/ui/SectionLabel'
import { usePerson } from '../contexts/PersonContext'
import { useReportSubmission } from '../hooks/useReportSubmission'
import { useCircles } from '../hooks/useCircles'
import { ReportAttachments, ReportEvidenceStatus } from '../components/safety/ReportAttachments'
import type { CotReportReason } from '../types/community'

const REASONS: Array<{ value: CotReportReason; label: string; hint: string }> = [
  { value: 'Unkind', label: 'Unkind to someone', hint: 'Mean, cruel, or leaving someone out on purpose.' },
  { value: 'PrivateInfo', label: 'Asked for private information', hint: 'Wanted a real name, school, address, or an off-site app.' },
  { value: 'ScaryAdult', label: 'Said something scary or adult', hint: 'Frightening, sexual, or clearly not a child.' },
  { value: 'Griefing', label: 'Wrecked or stole on purpose', hint: 'Destroyed a build or took things to be cruel.' },
  { value: 'Stranger', label: 'Brought in someone unvetted', hint: 'Pulled in a player nobody in the circle vouched for.' },
]

interface ReportPrefill {
  targetName?: string
  targetPersonId?: string
  targetHandle?: string
  circleId?: string
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const validId = (value: unknown) => typeof value === 'string' && uuid.test(value) ? value : undefined

/** Report a member (COTC_SafetyKudos R4 / DESIGN §3.8). Full-page form, both roles. */
export function ReportPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as ReportPrefill | null) ?? {}
  const params = new URLSearchParams(location.search)
  const prefill: ReportPrefill = { targetName: typeof state.targetName === 'string' ? state.targetName : undefined, targetPersonId: validId(state.targetPersonId ?? params.get('targetPersonId')), targetHandle: typeof state.targetHandle === 'string' ? state.targetHandle : (params.get('handle') ?? params.get('targetHandle') ?? undefined), circleId: validId(state.circleId ?? params.get('circleId')) }
  const { guardianFirst, role, flags } = usePerson()
  const submission = useReportSubmission()
  const circles = useCircles()
  const [circleId, setCircleId] = useState(prefill.circleId ?? '')
  const selectedCircle = circles.data?.find(circle => circle.id === circleId)
  const [fileError, setFileError] = useState<string | null>(null)

  const [handle, setHandle] = useState(prefill.targetHandle ?? '')
  const [reason, setReason] = useState<CotReportReason | null>(null)
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [detail, setDetail] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [blockRequested, setBlockRequested] = useState(true)
  const [notifyCircle, setNotifyCircle] = useState(false)

  const targetPersonId = prefill.targetPersonId
  const targetName = prefill.targetName ?? (handle.trim() || null)
  const hasTarget = Boolean(targetPersonId) || handle.trim().length > 0

  async function send() {
    if (!hasTarget || !reason) return
    await submission.send({ circleId: selectedCircle?.id, targetPersonId, targetHandle: targetPersonId ? undefined : handle.trim(), reason, detail: detail.trim() || undefined, incidentDate: date || undefined, incidentTime: time || undefined, blockRequested, notifyCircle: Boolean(selectedCircle && notifyCircle) }, files)
  }

  function reset() {
    setHandle(prefill.targetHandle ?? ''); setReason(null); setDate(''); setTime('')
    setDetail(''); setFiles([]); setBlockRequested(true); setNotifyCircle(false); submission.reset(); setFileError(null)
  }

  if (submission.receipt) {
    return (
      <div className="animate-rise px-4 py-6 sm:px-10 sm:py-9">
        <Card tone="panel" className="max-w-[560px] bg-moss-bg">
          <h1 className="font-serif text-[28px] font-medium text-moss">Sent. Thank you.</h1>
          <p className="mt-3 text-[14px] leading-relaxed text-ink-muted">
            Your report was received and queued for review.
            {submission.receipt.blockApplied === true && targetName ? ` Your family will no longer see ${targetName} in supported community views.` : ''}
            {submission.receipt.circleNotified === true ? ' A safety notice was added to the circle feed, without naming who reported.' : ' No circle notification is confirmed.'}
          </p>
          {role === 'kid' && <p className="mt-3 text-[13px] text-ink-muted">{guardianFirst || 'Your grown-up'} can track this report in the safety centre.</p>}
          <ReportEvidenceStatus failedFiles={submission.failedFiles} uploadedCount={submission.uploadedCount} busy={submission.busy} onRemove={submission.removeFailedFile} onRetry={() => void submission.retryAttachments()} />
          <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="outline" disabled={submission.busy} onClick={reset}>Report someone else</Button>
            <Button variant="ink" disabled={submission.busy} onClick={() => navigate(role === 'kid' ? '/' : '/safety')}>{role === 'kid' ? 'Done' : 'Track it in the safety centre'}</Button>
          </div>
        </Card>
      </div>
    )
  }

  const intro = role === 'kid' && (flags.supervised || flags.viewOnly)
    ? `You will never get in trouble for reporting. It is queued for moderator review, and ${guardianFirst || 'a grown-up'} can see it too.`
    : 'Reports go to a moderator, not to the person you name. You choose whether the rest of the circle is told.'

  return (
    <div className="animate-rise px-4 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">Report</div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">Report a member</h1>
      <p className="mt-3 max-w-[62ch] text-[15px] leading-relaxed text-ink-muted">{intro}</p>

      <div className="mt-7 grid max-w-[640px] gap-6">
        {/* 1 · Who */}
        <section>
          <SectionLabel>1 · Who are you reporting?</SectionLabel>
          {targetPersonId ? (
            <p className="mt-2 text-[14px] text-ink">{prefill.targetName ?? 'The player you selected'}</p>
          ) : (
            <Input
              className="mt-2"
              disabled={submission.busy}
              aria-label="Their in-game name"
              placeholder="Their in-game name"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
            />
          )}
          <p className="mt-1 text-[12px] text-ink-muted">Report a player by the name you see in the game — you do not need their real name.</p>
        </section>

        {/* 2 · Reason */}
        <section>
          <SectionLabel>2 · What happened?</SectionLabel>
          <div className="mt-2 grid gap-2">
            {REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                aria-pressed={reason === r.value}
                disabled={submission.busy}
                onClick={() => setReason(r.value)}
                className={`rounded-btn border px-4 py-3 text-left transition-colors ${reason === r.value ? 'border-terra bg-honey-bg' : 'border-line hover:border-terra'}`}
              >
                <div className="text-[13.5px] font-medium text-ink">{r.label}</div>
                <div className="mt-0.5 text-[12px] text-ink-muted">{r.hint}</div>
              </button>
            ))}
          </div>
        </section>

        {/* 3 · When */}
        <section>
          <SectionLabel>3 · When did it happen?</SectionLabel>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input disabled={submission.busy} aria-label="Incident date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-input border border-line bg-paper-surface px-3 py-2 text-[13px]" />
            <input disabled={submission.busy} aria-label="Incident time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-input border border-line bg-paper-surface px-3 py-2 text-[13px]" />
          </div>
          <p className="mt-1 text-[12px] text-ink-muted">Roughly is fine — it helps the moderator pull the right slice of the room log.</p>
        </section>

        {/* 4 · Detail + attachments */}
        <section>
          <SectionLabel>4 · Anything else? Optional</SectionLabel>
          <TextArea disabled={submission.busy} label="Report details" className="mt-2" rows={3} placeholder="Add anything that helps a moderator understand." value={detail} onChange={(e) => setDetail(e.target.value)} />
          <ReportAttachments files={files} onChange={setFiles} onError={setFileError} disabled={submission.busy} />
          {fileError && <p role="alert" className="mt-2 text-[13px] text-honey-fg">{fileError}</p>}
        </section>

        <section>
          <label className="block text-[13px] text-ink" htmlFor="report-circle">Circle context (optional)</label>
          <select id="report-circle" value={selectedCircle ? circleId : ''} disabled={submission.busy} onChange={e => { setCircleId(e.target.value); setNotifyCircle(false) }} className="mt-2 w-full min-w-0 rounded-input border border-line bg-paper-surface px-3 py-2 text-[13px]">
            <option value="">No circle selected</option>{(circles.data ?? []).map(circle => <option key={circle.id} value={circle.id}>{circle.name}</option>)}
          </select>
          {circles.isError && <p role="alert" className="mt-2 text-[13px] text-honey-fg">Could not load your circles. You can still report without a circle. <button type="button" onClick={() => void circles.refetch()} className="underline">Retry circles</button></p>}
        </section>

        {/* Toggles */}
        <section className="grid gap-2">
          <label className="flex items-center gap-2 text-[13.5px] text-ink">
            <input type="checkbox" disabled={submission.busy} checked={blockRequested} onChange={(e) => setBlockRequested(e.target.checked)} />
            Hide this player from our family’s community views
          </label>
          <label className="flex items-center gap-2 text-[13.5px] text-ink">
            <input type="checkbox" disabled={!selectedCircle || submission.busy} checked={Boolean(selectedCircle && notifyCircle)} onChange={(e) => setNotifyCircle(e.target.checked)} />
            Tell the other parents in this circle
          </label>
        </section>

        {/* Summary + send */}
        <div className="border-t border-line-row pt-4">
          <p className="text-[13px] text-ink-muted">
            {hasTarget && reason
              ? `Reporting ${targetName}. A moderator sees your name; ${targetName} never does.`
              : 'Pick a member and a reason to send. Everything else is optional.'}
          </p>
          {submission.error && <p role="alert" className="mt-2 text-[13px] text-honey-fg">{submission.error} Delivery could not be confirmed. Please check your reports before retrying.</p>}
          <GatedButton
            className="mt-3"
            disabled={submission.busy}
            gated={!hasTarget || !reason}
            hint="Pick who you are reporting and what happened."
            variant="accent"
            onClick={send}
          >
            {submission.busy ? 'Sending report and attachments…' : 'Send to a moderator'}
          </GatedButton>
        </div>
      </div>
    </div>
  )
}
